// Voucher Category Group API functions
import { getUserInfo } from './auth';
import { CommonUtils, parseApiCountMapFromResponse } from './commonUtils';
import { getApiV1Url } from '../config/api';

// ============================================================================
// INTERFACES
// ============================================================================

// 카테고리/그룹 생성 요청
export interface CategoryGroupCreateRequest {
  brandCode?: string;
  code: string;
  status: string;
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
}

// 카테고리/그룹 생성 응답
export interface CategoryGroupCreateResponse {
  success: boolean;
  message: string;
  data?: {
    code: string;
    title: string;
  };
}

// 카테고리/그룹 정보
export interface CategoryGroup {
  code: string;
  title: string;
  brandCode?: string;
  brandName?: string;
  status: string;
  type: string;
  subtitle?: string;
  description?: string;
  voucherCount?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  tree?: CategoryGroupTreeNode[];
}

// 카테고리/그룹 상세 정보
export interface CategoryGroupDetail extends CategoryGroup {}

// 카테고리/그룹 수정 요청
export interface CategoryGroupUpdateRequest {
  brandCode?: string;
  code: string;
  status: string;
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
}

// 카테고리/그룹 수정 응답
export interface CategoryGroupUpdateResponse {
  success: boolean;
  message: string;
  data?: {
    code: string;
    title: string;
  };
}

// 카테고리/그룹 목록 요청
export interface CategoryGroupListRequest {
  page?: number;
  size?: number;
  sort?: string;
  keyword?: string;
  status?: string;
  brandCode?: string;
  type?: string;
}

// 카테고리/그룹 목록 응답
export interface CategoryGroupListResponse {
  totalCount: number;
  voucherGroups: CategoryGroup[];
  totalPages?: number;
  currentPage?: number;
  pageSize?: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

// 카테고리/그룹 트리 노드
export interface CategoryGroupTreeNode {
  code: string;
  status: string;
  depth: number;
  count: number;
  title: string;
  subtitle?: string;
  logo?: string;
  sub?: CategoryGroupTreeNode[];
}

// 카테고리/그룹 트리 업데이트 요청
export interface CategoryGroupTreeUpdateRequest {
  tree: CategoryGroupTreeNode[];
}

// 카테고리/그룹 트리 업데이트 응답
export interface CategoryGroupTreeUpdateResponse {
  success: boolean;
  message: string;
  data?: {
    tree: CategoryGroupTreeNode[];
  };
}

// 교환권-트리 노드 연동 요청 (추가/순서변경: update, 삭제: remove)
export interface VoucherTreeOperationRequest {
  operation: 'update' | 'remove';
  treeCode: string;
  sortOrder?: string;
}

// 트리-교환권 목록 항목 (tree-vouchers API 응답)
export interface TreeVoucherItem {
  voucherCode: string;
  sortOrder: string;
}

// 트리-교환권 목록 응답 (노드에 포함된 교환권 목록)
export interface TreeVouchersResponse {
  vouchers: TreeVoucherItem[];
}

/** 트리에서 노드코드까지의 경로(타이틀 배열) 반환. 루트 → 해당 노드 순서. 없으면 null */
export function findPathToNode(
  nodes: CategoryGroupTreeNode[],
  targetCode: string,
  pathSoFar: string[] = []
): string[] | null {
  for (const node of nodes) {
    const currentPath = [...pathSoFar, node.title];
    if (node.code === targetCode) return currentPath;
    if (node.sub?.length) {
      const found = findPathToNode(node.sub, targetCode, currentPath);
      if (found) return found;
    }
  }
  return null;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

// 카테고리/그룹 생성 API
export const createCategoryGroup = async (
  projectCode: string,
  categoryGroupData: CategoryGroupCreateRequest
): Promise<CategoryGroupCreateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/voucher-groups`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(categoryGroupData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('카테고리/그룹 생성 API 응답:', result);

    return {
      success: true,
      message: result.message || '카테고리/그룹이 성공적으로 생성되었습니다.',
      data: result.data || result
    };
  } catch (error: any) {
    console.error('카테고리/그룹 생성 중 오류 발생:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }

    throw error;
  }
};

// 카테고리/그룹 목록 조회 API
export const getCategoryGroupList = async (
  projectCode: string,
  params: CategoryGroupListRequest = {}
): Promise<CategoryGroupListResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };
    
    // 쿼리 파라미터 구성
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.keyword) queryParams.append('keyword', params.keyword);
    if (params.status) queryParams.append('status', params.status);
    if (params.brandCode) queryParams.append('brandCode', params.brandCode);
    if (params.type) queryParams.append('type', params.type);

    const queryString = queryParams.toString();
    const url = getApiV1Url(`/projects/${projectCode}/voucher-groups${queryString ? `?${queryString}` : ''}`);

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('카테고리/그룹 목록 API 응답:', result);

    // 응답 데이터 구조에 따라 처리
    const voucherGroups = result.data?.voucherGroups || [];
    const totalCount = result.data?.totalCount || voucherGroups.length;

    return {
      totalCount,
      voucherGroups,
      totalPages: result.data?.totalPages || result.totalPages,
      currentPage: result.data?.currentPage || result.currentPage,
      pageSize: result.data?.pageSize || result.pageSize,
      hasNext: result.data?.hasNext || result.hasNext,
      hasPrevious: result.data?.hasPrevious || result.hasPrevious
    };
  } catch (error: any) {
    console.error('카테고리/그룹 목록 조회 중 오류 발생:', error);

    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }

    throw error;
  }
};

// 그룹코드별 교환권 수 조회 응답: { timestamp?, data: { counts: Map<String, Long> } }
export interface VoucherCountByGroupCodesResponse {
  timestamp?: number;
  data?: { counts: Record<string, number> };
}

// 그룹코드별 교환권 수 조회 (목록용)
export const getVoucherCountByGroupCodes = async (
  projectCode: string,
  groupCodes: string[]
): Promise<Record<string, number>> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };
    const queryParams = new URLSearchParams();
    groupCodes.forEach(code => queryParams.append('groupCodes', code));
    const queryString = queryParams.toString();
    const url = getApiV1Url(`/projects/${projectCode}/voucher-stats/count/group-codes${queryString ? `?${queryString}` : ''}`);

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result: VoucherCountByGroupCodesResponse = await response.json();
    return parseApiCountMapFromResponse(result);
  } catch (error: any) {
    console.error('그룹코드별 교환권 수 조회 중 오류:', error);
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};

// 노드코드별(treeCode) 교환권 수 조회 응답: { timestamp?, data: { counts: Map<String, Long> } }
export interface VoucherCountByTreeCodesResponse {
  timestamp?: number;
  data?: { counts: Record<string, number> };
}

// 노드코드별(treeCode) 교환권 수 조회 (트리 설정용)
// GET /projects/{projectCode}/voucher-stats/count/tree-codes?treeCodes=...
// treeCode 형식: 그룹코드:노드코드
export const getVoucherCountByTreeCodes = async (
  projectCode: string,
  treeCodes: string[]
): Promise<Record<string, number>> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };
    const queryParams = new URLSearchParams();
    treeCodes.forEach(code => queryParams.append('treeCodes', code));
    const queryString = queryParams.toString();
    const url = getApiV1Url(`/projects/${projectCode}/voucher-stats/count/tree-codes${queryString ? `?${queryString}` : ''}`);

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result: VoucherCountByTreeCodesResponse = await response.json();
    return parseApiCountMapFromResponse(result);
  } catch (error: any) {
    console.error('노드코드별 교환권 수 조회 중 오류:', error);
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};

// 카테고리/그룹 상세 조회 API
export const getCategoryGroupDetail = async (
  projectCode: string,
  categoryGroupCode: string
): Promise<CategoryGroupDetail> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };
    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/voucher-groups/${categoryGroupCode}`),
      { method: 'GET', headers }
    );
    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }
    const result = await response.json();
    console.log('카테고리/그룹 상세 API 응답:', result);
    return result.data || result;
  } catch (error: any) {
    console.error('카테고리/그룹 상세 조회 중 오류 발생:', error);
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};

// 카테고리/그룹 수정 API
export const updateCategoryGroup = async (
  projectCode: string,
  categoryGroupCode: string,
  categoryGroupData: CategoryGroupUpdateRequest
): Promise<CategoryGroupUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/voucher-groups/${categoryGroupCode}`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(categoryGroupData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('카테고리/그룹 수정 API 응답:', result);

    return {
      success: true,
      message: result.message || '카테고리/그룹이 성공적으로 수정되었습니다.',
      data: result.data || result
    };
  } catch (error: any) {
    console.error('카테고리/그룹 수정 중 오류 발생:', error);

    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};

// 카테고리/그룹 트리 업데이트 API
export const updateCategoryGroupTree = async (
  projectCode: string,
  groupCode: string,
  tree: CategoryGroupTreeNode[]
): Promise<CategoryGroupTreeUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const requestBody: CategoryGroupTreeUpdateRequest = {
      tree: tree
    };

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/voucher-groups/${groupCode}/tree`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('카테고리/그룹 트리 업데이트 API 응답:', result);

    return {
      success: true,
      message: result.message || '트리가 성공적으로 업데이트되었습니다.',
      data: result.data || result
    };
  } catch (error: any) {
    console.error('카테고리/그룹 트리 업데이트 중 오류 발생:', error);

    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};

// 트리별 교환권 목록 조회 API (노드에 포함된 교환권 목록)
// GET /projects/{projectCode}/voucher-tree-vouchers?treeCodes=...
export const getTreeVouchers = async (
  projectCode: string,
  treeCodes: string[]
): Promise<TreeVouchersResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const queryParams = new URLSearchParams();
    treeCodes.forEach(code => queryParams.append('treeCodes', code));
    const queryString = queryParams.toString();
    const url = getApiV1Url(`/projects/${projectCode}/tree-vouchers${queryString ? `?${queryString}` : ''}`);

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('[getTreeVouchers] API 원본 응답:', result);
    const rawVouchers = result.data?.vouchers ?? result.vouchers ?? [];
    const vouchers: TreeVoucherItem[] = rawVouchers.map((p: { code: string; treeSortInfo?: Record<string, string> }) => {
      const sortOrder = treeCodes[0] ? (p.treeSortInfo?.[treeCodes[0]] ?? '') : '';
      return { voucherCode: p.code, sortOrder };
    });
    return { vouchers };
  } catch (error: any) {
    console.error('트리 교환권 목록 조회 중 오류 발생:', error);
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};

// 교환권-트리 노드 연동 API (추가/순서변경: update, 삭제: remove)
// GET/POST/PUT 등 baseUrl + /projects/{projectCode}/vouchers/{voucherCode}/tree
export const updateVoucherInTree = async (
  projectCode: string,
  voucherCode: string,
  payload: VoucherTreeOperationRequest
): Promise<{ success: boolean; message?: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
    };

    const body: Record<string, string> = {
      operation: payload.operation,
      treeCode: payload.treeCode
    };
    if (payload.operation === 'update' && payload.sortOrder != null) body.sortOrder = payload.sortOrder;
    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/vouchers/${voucherCode}/tree`),
      {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json().catch(() => ({}));
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('교환권-트리 연동 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) throw new Error(CommonUtils.getNetworkErrorMessage(error));
    throw error;
  }
};